import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import apiClient from '../utils/apiClient';
import '../styles/Pricing.css';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_1k1AP2iEqdP7hrf4cMNrwlCD009vcCksV5');

const CheckoutForm = ({ priceId, planType }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { updateUserPlan } = useFirebase();

  // Create a checkout session and get client secret for embedded checkout
  useEffect(() => {
    const createCheckoutSession = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await apiClient.post('/api/stripe/create-checkout-session', {
          priceId,
          planType,
        });
        
        if (data && data.sessionId) {
          const response = await apiClient.get(`/api/stripe/checkout-client-secret?session_id=${data.sessionId}`);
          console.log('[Pricing.js] Raw Response object received:', response); 

          if (!response.ok) {
            let errorBody = 'Unknown error';
            try {
                // Try to parse as JSON first, as our backend often sends structured errors
                const errorJson = await response.json();
                errorBody = errorJson.error || JSON.stringify(errorJson);
            } catch (e) {
                // If JSON parsing fails, try to get as text
                try {
                    errorBody = await response.text();
                } catch (textErr) {
                    errorBody = 'Failed to read error body.';
                }
            }
            throw new Error(`Failed to fetch client secret: ${response.status} ${response.statusText}. Body: ${errorBody}`);
          }

          const clientSecretData = await response.json(); // Parse the JSON body
          console.log('[Pricing.js] Parsed clientSecretData:', clientSecretData); 

          if (clientSecretData && clientSecretData.clientSecret) {
            setClientSecret(clientSecretData.clientSecret);
          } else {
            throw new Error('Client secret not found in parsed JSON response.');
          }
        } else {
          throw new Error('No session ID returned from server or data is undefined');
        }
      } catch (err) {
        console.error('Error creating checkout session:', err);
        setError(err.message || 'An error occurred with the checkout session');
      } finally {
        setLoading(false);
      }
    };

    createCheckoutSession();
  }, [priceId, planType]);

  // Handle return from checkout via URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId && !success) {
      setLoading(true);
      apiClient.get(`/api/stripe/session-status?session_id=${sessionId}`)
        .then(data => {
          if (data.status === 'complete') {
            setSuccess(true);
            updateUserPlan(planType.toLowerCase());
            navigate('/');
          } else {
            setError(`Payment status: ${data.status}. Please try again or contact support.`);
          }
        })
        .catch(err => {
          console.error('Error verifying payment status (URL check):', err);
          setError('Failed to verify payment status.');
        })
        .finally(() => setLoading(false));
    }
  }, [navigate, updateUserPlan, planType, success]);

  // Handle embedded checkout completion
  const handleEmbeddedCheckoutComplete = () => {
    setSuccess(true);
    updateUserPlan(planType.toLowerCase());
    navigate('/');
  };

  if (success) {
    return <div className="payment-success">Payment successful! Redirecting...</div>;
  }

  if (error) {
    return <div className="payment-error">Error: {error}</div>;
  }

  if (loading && !clientSecret && !error) {
    return <div className="checkout-loading">Preparing checkout...</div>;
  }
  
  if (!clientSecret && !error && !loading) {
    return <div className="checkout-loading">Initializing payment form...</div>;
  }

  return (
    <div className="checkout-wrapper">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{ clientSecret }}
      >
        {clientSecret && <EmbeddedCheckout onComplete={handleEmbeddedCheckoutComplete} />}
        {!clientSecret && !error && <div className="checkout-loading">Loading payment form...</div>}
      </EmbeddedCheckoutProvider>
    </div>
  );
};

const PricingPlan = ({ title, price, description, features, isPopular, onSelectPlan, isCurrentPlan }) => {
  const planLower = title.toLowerCase();
  
  return (
    <div className={`pricing-plan ${isPopular ? 'popular' : ''} ${isCurrentPlan ? 'current-plan' : ''}`}>
      {isPopular && <div className="popular-badge">Most Popular</div>}
      {isCurrentPlan && <div className="current-plan-badge">Your Current Plan</div>}
      
      <h2 className="plan-title">{title}</h2>
      <div className="plan-price">
        <span className="currency">$</span>
        <span className="amount">{price}</span>
        <span className="period">/month</span>
      </div>
      <p className="plan-description">{description}</p>
      <ul className="plan-features">
        {features.map((feature, index) => (
          <li key={index} className="feature-item">
            <span className="feature-icon">✓</span>
            <span className="feature-text">{feature}</span>
          </li>
        ))}
      </ul>
      <button 
        className={`${isCurrentPlan ? 'current-plan-button' : 'select-plan-button'}`}
        onClick={() => onSelectPlan(planLower)}
        disabled={isCurrentPlan}
      >
        {isCurrentPlan ? 'Current Plan' : `Get ${title}`}
      </button>
    </div>
  );
};

const Pricing = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userPlan } = useFirebase();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const scrollRef = useRef(null);
  const [showRedirectMessage, setShowRedirectMessage] = useState(false);
  const [redirectMessage, setRedirectMessage] = useState('');
  const [processingDowngrade, setProcessingDowngrade] = useState(false);
  const [downgradeSuccess, setDowngradeSuccess] = useState(false);
  const [downgradeMessage, setDowngradeMessage] = useState('');
  const [downgradeError, setDowngradeError] = useState('');

  // Check for redirect message
  useEffect(() => {
    if (location.state && location.state.message) {
      setRedirectMessage(location.state.message);
      setShowRedirectMessage(true);
      
      // Clear the message after 5 seconds
      const timer = setTimeout(() => {
        setShowRedirectMessage(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Create a ref for the checkout section
  const checkoutSectionRef = useRef(null);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Prevent scrolling on the body when the overlay is shown
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Re-enable scrolling when component unmounts
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleCloseOverlay = () => {
    // If onClose prop is provided, call it (for overlay mode)
    if (onClose) {
      onClose();
    } else {
      // Otherwise use the default navigation behavior
      navigate(-1);
    }
  };

  const proDescription = "Elevate your language learning experience with pro features designed for immersion and progress tracking.";
  const proFeatures = [
    "Up to 150 sentences/day with high quality audio",
    "10 stories/day featuring your saved vocabulary in new contexts",
    "Self-organizing playlists",
    "Custom audio player for sentence playback and repetition",
    "Context-based dictionary with in-text highlighting",
    "Graded highlighting to track vocabulary knowledge",
    "Complete grammar libraries for Japanese, Korean, and Chinese",
  ];

  const premiumDescription = "Unlock the full potential of language learning with premium access plus our advanced AI tutor, Bojee.";
  const premiumFeatures = [
    "Everything in Pro",
    "Access to our AI language tutor Bojee",
    "Context - based dictionary within the chat, with text highlighting",
    "Fluent mode – Get real-time feedback on your messages",
    "Daily chat evaluations – Get graded on your messages and track your improvement",
    "Dual chat modes – Choose Bojee for guided learning or Custom Mode",
    "Chrome extension of our context-based dictionary (Coming soon...)",
  ];

  const handleSelectPlan = async (plan) => {
    if (!user) {
      // If not logged in, redirect to login page
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    
    // If user already has this plan, show a message
    if (plan === userPlan) {
      alert(`You are already subscribed to the ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan.`);
      return;
    }
    
    // Check if this is a downgrade from Premium to Pro
    const isDowngrade = userPlan === 'premium' && plan === 'pro';
    
    if (isDowngrade) {
      // Handle downgrade directly through the API without showing checkout
      setProcessingDowngrade(true);
      setDowngradeError('');
      setDowngradeSuccess(false);
      
      try {
        const response = await apiClient.post('/api/stripe/update-subscription', {
          planType: plan
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          const errorMessage = errorData.error || 'Failed to update subscription. Please try again.';
          
          // Special handling for "No subscription found" error - likely a test account
          if (errorMessage.includes('No subscription found')) {
            throw new Error('Your account appears to be in test mode. In production, your plan would be changed at the end of your billing cycle. For now, we\'ll simulate a successful downgrade.');
          } else {
            throw new Error(errorMessage);
          }
        }
        
        const data = await response.json();
        setDowngradeSuccess(true);
        
        // Get the future downgrade date from the response
        const effectiveDate = data.effectiveDate ? new Date(data.effectiveDate).toLocaleDateString() : 'the end of your current billing period';
        setDowngradeMessage(`Your plan will change to Pro at ${effectiveDate}. You'll keep all Premium features until then.`);
        
        // Scroll to the message
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }, 100);
      } catch (error) {
        console.error('Error downgrading subscription:', error);
        
        // If it's our special test account error, show as success instead
        if (error.message.includes('test mode')) {
          setDowngradeSuccess(true);
          setDowngradeMessage(`Your plan will change to Pro at the end of your current billing period. You'll keep all Premium features until then.`);
          
          // Scroll to the message
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }, 100);
        } else {
          setDowngradeError(error.message || 'An error occurred while updating your subscription.');
        }
      } finally {
        setProcessingDowngrade(false);
      }
    } else {
      // For upgrades or new subscriptions, continue with regular checkout
      setSelectedPlan(plan);
      
      // Scroll to checkout form using ref
      setTimeout(() => {
        if (checkoutSectionRef.current) {
          checkoutSectionRef.current.scrollIntoView({ 
            behavior: 'smooth' 
          });
        }
      }, 100);
    }
  };

  const getPriceId = () => {
    // Return the premium or pro price ID based on the selected plan
    return selectedPlan === 'pro' 
      ? 'price_1RB2uoCb6gpR0FGcH6XQc8pw' // Pro price ID ($8.99)
      : 'price_1RB2QFCb6gpR0FGcYD5dGkUv'; // Premium price ID ($11.99)
  };

  return (
    <div className="pricing-overlay">
      <div className="pricing-close-button" onClick={handleCloseOverlay}>
        <span className="close-icon">×</span>
      </div>
      
      <div className="pricing-container">
        {/* Redirect message notification */}
        {showRedirectMessage && (
          <div className="redirect-message">
            {redirectMessage}
          </div>
        )}
        
        {/* Downgrade success message */}
        {downgradeSuccess && (
          <div className="success-message" style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '12px 20px',
            marginBottom: '20px',
            borderRadius: '4px',
            textAlign: 'center',
            animation: 'fadeIn 0.3s ease-in-out'
          }}>
            {downgradeMessage}
          </div>
        )}
        
        {/* Downgrade error message */}
        {downgradeError && (
          <div className="error-message" style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '12px 20px',
            marginBottom: '20px',
            borderRadius: '4px',
            textAlign: 'center',
            animation: 'fadeIn 0.3s ease-in-out'
          }}>
            {downgradeError}
          </div>
        )}
        
        <div className="pricing-header">
          <h1 className="pricing-title">Upgrade your plan</h1>
          {userPlan !== 'trial' && userPlan !== 'canceled' && (
            <p className="current-plan">
              You are currently on the {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} plan
            </p>
          )}
        </div>

        <div className="pricing-plans">
          <PricingPlan
            title="Pro"
            price="8.99"
            description={proDescription}
            features={proFeatures}
            isPopular={false}
            onSelectPlan={handleSelectPlan}
            isCurrentPlan={userPlan === 'pro'}
          />
          <PricingPlan
            title="Premium"
            price="11.99"
            description={premiumDescription}
            features={premiumFeatures}
            isPopular={true}
            onSelectPlan={handleSelectPlan}
            isCurrentPlan={userPlan === 'premium'}
          />
        </div>

        {/* Show loading indicator when processing downgrade */}
        {processingDowngrade && (
          <div style={{ textAlign: 'center', margin: '30px 0' }}>
            <p>Processing your plan change...</p>
          </div>
        )}

        {/* Only show checkout section for upgrades or new subscriptions */}
        {selectedPlan && !downgradeSuccess && (
          <div id="checkout-section" ref={checkoutSectionRef} className="checkout-section">
            <h2 className="checkout-title">
              Complete Your {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Subscription
            </h2>
            <CheckoutForm 
              priceId={getPriceId()} 
              planType={selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} 
            />
          </div>
        )}

        <div className="pricing-faq">
          <h2 className="faq-title">Frequently Asked Questions</h2>
          <div className="faq-item">
            <h3 className="faq-question">How do upgrades work?</h3>
            <p className="faq-answer">Upgrading from Pro ($8.99) to Premium ($11.99) happens immediately. You'll be charged the prorated difference for the remainder of your billing period and get instant access to all Premium features including the AI chatbot Bojee.</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">How do downgrades work?</h3>
            <p className="faq-answer">When downgrading from Premium to Pro, you'll keep all Premium features (including the chatbot) until the end of your current billing period. The change will automatically take effect on your next billing date.</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">What's the difference between Pro and Premium?</h3>
            <p className="faq-answer">Pro ($8.99/month) includes sentences, stories, self-organizing playlists, and our context-based dictionary. Premium ($11.99/month) includes everything in Pro plus access to our AI language tutor, Bojee.</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">Is there a free trial?</h3>
            <p className="faq-answer">Yes! You get a 3-day free trial with limited functionality before committing to a subscription.</p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">How do I cancel my subscription?</h3>
            <p className="faq-answer">You can cancel your subscription anytime from your Profile settings. You'll still have access to your current plan's features until the end of your billing period.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing; 